export function dateStringToDate(value?: string | null): Date | undefined {
  if (!value) return undefined;
  const datePart = value.includes('T') ? value.slice(0, 10) : value;
  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
}

export function dateToDateString(date?: Date): string {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function safeTimeZone(timeZone?: string | null): string | undefined {
  if (!timeZone) return undefined;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date(0));
    return timeZone;
  } catch {
    return undefined;
  }
}

export function dateKeyInTimeZone(value?: string | null, timeZone?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 10);

  const zone = safeTimeZone(timeZone);
  const parts = new Intl.DateTimeFormat('en-US', {
    ...(zone ? { timeZone: zone } : {}),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;
  return year && month && day ? `${year}-${month}-${day}` : value.slice(0, 10);
}

export function dateInTimeZone(value?: string | null, timeZone?: string | null): Date | undefined {
  const dateKey = dateKeyInTimeZone(value, timeZone);
  return dateKey ? dateStringToDate(dateKey) : undefined;
}

export function formatDateTimeInTimeZone(
  value?: string | null,
  timeZone?: string | null,
  options: Intl.DateTimeFormatOptions = {},
): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const zone = safeTimeZone(timeZone);
  return new Intl.DateTimeFormat('en-US', {
    ...(zone ? { timeZone: zone } : {}),
    ...options,
  }).format(date);
}

export function formatCalendarDate(
  value?: string | null,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
): string {
  const date = dateStringToDate(value);
  return date ? new Intl.DateTimeFormat('en-US', options).format(date) : '';
}

export function normalizeDocumentDateFields(values: {
  date?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}): { date: string; createdAt: string; updatedAt: string } {
  return {
    // Keep absence meaningful: callers use it to distinguish an office-selected
    // calendar date from the system creation instant fallback.
    date: values.date ?? '',
    createdAt: values.createdAt ?? values.date ?? '',
    updatedAt: values.updatedAt ?? values.createdAt ?? values.date ?? '',
  };
}

/** Format an invoice's explicit calendar date or its creation instant fallback. */
export function formatDocumentIssueDate(
  issueDate?: string | null,
  createdAt?: string | null,
  timeZone?: string | null,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
): string {
  if (issueDate) return formatCalendarDate(issueDate, options);
  return formatDateTimeInTimeZone(createdAt, timeZone, options);
}

/** "Friday, May 29 · 4:30 PM" in a tz (date · time) — the public-booking style.
 *  Always the entity/business tz, never the browser's, so pick == confirmation. */
export function formatWhenInTimeZone(
  value: string | null | undefined,
  timeZone: string | null | undefined,
  opts: { weekday?: 'long' | 'short'; month?: 'long' | 'short'; withTime?: boolean } = {},
): string {
  if (!value) return '';
  const date = formatDateTimeInTimeZone(value, timeZone, {
    weekday: opts.weekday ?? 'long',
    month: opts.month ?? 'long',
    day: 'numeric',
  });
  if (!date) return '';
  if (opts.withTime === false) return date;
  const time = formatDateTimeInTimeZone(value, timeZone, { hour: 'numeric', minute: '2-digit' });
  return `${date} · ${time}`;
}

/** The viewer's own browser timezone — the ONLY legitimate use of the browser
 *  tz (a secondary hint, never to store or interpret a business time). */
export function getViewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Short tz abbreviation for an instant in a zone, e.g. "CDT" (DST-aware). */
export function timeZoneAbbrev(timeZone?: string | null, at: Date = new Date()): string {
  const zone = safeTimeZone(timeZone);
  if (!zone) return '';
  const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
    .formatToParts(at)
    .find((p) => p.type === 'timeZoneName');
  return part?.value ?? '';
}

/** Human tz label, e.g. "CDT · America/Chicago" (abbrev + IANA). */
export function formatTimeZoneLabel(timeZone?: string | null, at: Date = new Date()): string {
  const zone = safeTimeZone(timeZone);
  if (!zone) return '';
  const abbrev = timeZoneAbbrev(zone, at);
  return abbrev ? `${abbrev} · ${zone}` : zone;
}

export function timeKeyInTimeZone(
  value?: string | null,
  timeZone?: string | null,
  fallback = '09:00',
): string {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  const zone = safeTimeZone(timeZone);
  const parts = new Intl.DateTimeFormat('en-US', {
    ...(zone ? { timeZone: zone } : {}),
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const hour = parts.find((part) => part.type === 'hour')?.value;
  const minute = parts.find((part) => part.type === 'minute')?.value;
  return hour && minute ? `${hour}:${minute}` : fallback;
}

function timeZoneOffsetMs(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const year = Number(parts.find((part) => part.type === 'year')?.value);
  const month = Number(parts.find((part) => part.type === 'month')?.value);
  const day = Number(parts.find((part) => part.type === 'day')?.value);
  const hour = Number(parts.find((part) => part.type === 'hour')?.value);
  const minute = Number(parts.find((part) => part.type === 'minute')?.value);
  const second = Number(parts.find((part) => part.type === 'second')?.value);
  return Date.UTC(year, month - 1, day, hour, minute, second) - date.getTime();
}

export function zonedDateTimeToUtc(
  date: Date | undefined,
  time: string,
  timeZone?: string | null,
): Date | undefined {
  if (!date || !/^\d{2}:\d{2}$/.test(time)) return undefined;
  const datePart = dateToDateString(date);
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);
  if (!year || !month || !day || Number.isNaN(hour) || Number.isNaN(minute)) return undefined;

  const wallClockUtc = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  const zone = safeTimeZone(timeZone);
  if (!zone) return new Date(wallClockUtc);

  const firstPass = new Date(wallClockUtc - timeZoneOffsetMs(new Date(wallClockUtc), zone));
  const secondOffset = timeZoneOffsetMs(firstPass, zone);
  return new Date(wallClockUtc - secondOffset);
}

export function timeFromLocalDateTime(value?: string | null, fallback = '09:00'): string {
  if (!value) return fallback;
  const timePart = value.includes('T') ? value.slice(11, 16) : '';
  return /^\d{2}:\d{2}$/.test(timePart) ? timePart : fallback;
}

export function setDatePart(value: string, date: Date | undefined, fallbackTime = '09:00'): string {
  const datePart = dateToDateString(date);
  if (!datePart) return '';
  return `${datePart}T${timeFromLocalDateTime(value, fallbackTime)}`;
}

export function setTimePart(value: string, time: string, fallbackDate?: string): string {
  const datePart = (value.includes('T') ? value.slice(0, 10) : value.slice(0, 10)) || fallbackDate;
  if (!datePart) return '';
  return `${datePart}T${time || '09:00'}`;
}
